"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FrameworkCombobox, FrameworksProvider } from "@/components/ui/FrameworkCombobox";

export const FrameworkComboboxExample: React.FC = () => {
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([]);
  const [selectedFrameworks2, setSelectedFrameworks2] = useState<string[]>([]);
  
  // Example of programmatically setting frameworks
  const frameworks = [
    { id: 'nist', name: 'NIST CSF', description: 'Cybersecurity framework', standard: 'NIST' },
    { id: 'iso27001', name: 'ISO 27001', description: 'Information security standard', standard: 'ISO' },
    { id: 'gdpr', name: 'GDPR', description: 'General Data Protection Regulation', standard: 'EU' },
    { id: 'hipaa', name: 'HIPAA', description: 'Health Insurance Portability and Accountability Act', standard: 'US' },
    { id: 'pci-dss', name: 'PCI DSS', description: 'Payment Card Industry Data Security Standard', standard: 'PCI' },
    { id: 'soc2', name: 'SOC 2', description: 'Service Organization Control 2', standard: 'AICPA' },
  ];

  // Example of handling framework selection changes
  const handleFrameworkSelect = (frameworkId: string) => {
    console.log('Framework selected:', frameworkId);
  };

  const handleFrameworkDeselect = (frameworkId: string) => {
    console.log('Framework deselected:', frameworkId);
  };

  const handleAllSelect = () => {
    console.log('All frameworks selected');
  };

  const handleAllDeselect = () => {
    console.log('All frameworks deselected');
  };

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>FrameworkCombobox Example</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="text-sm font-medium mb-2">Basic Usage</h3>
            <FrameworksProvider>
              <FrameworkCombobox
                selected={selectedFrameworks}
                setSelected={setSelectedFrameworks}
                placeholder="Select frameworks..."
                onFrameworkSelect={handleFrameworkSelect}
                onFrameworkDeselect={handleFrameworkDeselect}
                onAllSelect={handleAllSelect}
                onAllDeselect={handleAllDeselect}
              />
              <div className="mt-2 text-sm text-muted-foreground">
                Selected: {selectedFrameworks.length ? selectedFrameworks.join(', ') : 'None'}
              </div>
            </FrameworksProvider>
          </div>

          <div className="pt-4 border-t">
            <h3 className="text-sm font-medium mb-2">With Predefined Frameworks</h3>
            <FrameworkCombobox
              selected={selectedFrameworks2}
              setSelected={setSelectedFrameworks2}
              placeholder="Select frameworks..."
              frameworks={frameworks}
              className="w-full"
            />
            <div className="mt-2 text-sm text-muted-foreground">
              Selected: {selectedFrameworks2.length ? selectedFrameworks2.join(', ') : 'None'}
            </div>
          </div>

          <div className="pt-4 border-t">
            <h3 className="text-sm font-medium mb-2">Usage with Form</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                alert(`Selected frameworks: ${selectedFrameworks.join(', ')}`);
              }}
              className="space-y-4"
            >
              <FrameworksProvider>
                <FrameworkCombobox
                  selected={selectedFrameworks}
                  setSelected={setSelectedFrameworks}
                  placeholder="Select frameworks..."
                  className="w-full"
                />
              </FrameworksProvider>
              <Button type="submit">Submit</Button>
            </form>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Code Example</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="bg-muted p-4 rounded-md text-sm overflow-x-auto">
            {`// Basic usage with FrameworksProvider
<FrameworksProvider>
  <FrameworkCombobox
    selected={selectedFrameworks}
    setSelected={setSelectedFrameworks}
    placeholder="Select frameworks..."
  />
</FrameworksProvider>

// With predefined frameworks
<FrameworkCombobox
  selected={selectedFrameworks}
  setSelected={setSelectedFrameworks}
  frameworks={[
    { id: 'nist', name: 'NIST CSF', description: 'Cybersecurity framework' },
    { id: 'iso27001', name: 'ISO 27001', description: 'Information security standard' },
    // ...more frameworks
  ]}
  placeholder="Select frameworks..."
/>

// With event handlers
<FrameworkCombobox
  selected={selectedFrameworks}
  setSelected={setSelectedFrameworks}
  onFrameworkSelect={(id) => console.log('Selected:', id)}
  onFrameworkDeselect={(id) => console.log('Deselected:', id)}
  onAllSelect={() => console.log('All selected')}
  onAllDeselect={() => console.log('All deselected')}
  placeholder="Select frameworks..."
/>`}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
};

export default FrameworkComboboxExample;
